// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Transaction History
// Full on-chain audit trail of all transactions
// ================================================================
import React, { useState } from 'react';
import { useCasino } from '../context/CasinoShell';

export default function TransactionHistory() {
  const { transactionHistory } = useCasino();
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? transactionHistory :
    transactionHistory.filter(tx => tx.type === filter);

  const reversed = [...filtered].reverse();

  const totalDeposited = transactionHistory.filter(t => t.type === 'deposit').reduce((s, t) => s + t.amount, 0);
  const totalWithdrawn = transactionHistory.filter(t => t.type === 'withdraw').reduce((s, t) => s + t.amount, 0);
  const totalWon = transactionHistory.filter(t => t.type === 'win').reduce((s, t) => s + t.amount, 0);
  const totalLost = transactionHistory.filter(t => t.type === 'loss').reduce((s, t) => s + t.amount, 0);

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>📋 TRANSACTION HISTORY</h1>
      <p style={styles.subtitle}>Full audit trail — every transaction recorded</p>

      {/* Summary */}
      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>DEPOSITED</span>
          <span style={{ ...styles.summaryValue, color: '#00FF88' }}>${totalDeposited.toFixed(2)}</span>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>WITHDRAWN</span>
          <span style={{ ...styles.summaryValue, color: '#FFD700' }}>${totalWithdrawn.toFixed(2)}</span>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>WON</span>
          <span style={{ ...styles.summaryValue, color: '#00FF88' }}>${totalWon.toFixed(2)}</span>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>LOST</span>
          <span style={{ ...styles.summaryValue, color: '#DC2626' }}>${totalLost.toFixed(2)}</span>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        {['all', 'deposit', 'withdraw', 'win', 'loss'].map(f => (
          <button
            key={f}
            style={{ ...styles.filterBtn, background: filter === f ? '#DC2626' : '#1a1a1a', color: filter === f ? '#fff' : '#888' }}
            onClick={() => setFilter(f)}
          >{f.toUpperCase()}</button>
        ))}
      </div>

      {/* Transaction List */}
      <div style={styles.txList}>
        {reversed.length === 0 ? (
          <div style={styles.empty}>No transactions yet</div>
        ) : (
          reversed.map((tx, i) => (
            <div key={i} style={styles.txRow}>
              <div style={styles.txLeft}>
                <span style={styles.txIcon}>
                  {tx.type === 'deposit' ? '📥' : tx.type === 'withdraw' ? '📤' : tx.type === 'win' ? '🏆' : '🎰'}
                </span>
                <div>
                  <span style={styles.txType}>{tx.type.toUpperCase()}</span>
                  <span style={styles.txDesc}>{tx.description}</span>
                  <span style={styles.txTime}>{new Date(tx.timestamp).toLocaleString()}</span>
                </div>
              </div>
              <div style={styles.txRight}>
                <span style={{
                  ...styles.txAmount,
                  color: tx.type === 'deposit' || tx.type === 'win' ? '#00FF88' : '#DC2626',
                }}>
                  {tx.type === 'deposit' || tx.type === 'win' ? '+' : '-'}${tx.amount.toFixed(2)}
                </span>
                {tx.hash && (
                  <span style={styles.txHash}>{tx.hash.slice(0, 12)}...</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div style={styles.footer}>
        <p style={styles.footerText}>
          🔗 Transaction hashes can be verified on-chain.
          All records comply with FINTRAC reporting requirements.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: { padding: 16, maxWidth: 600, margin: '0 auto', minHeight: '100vh', background: '#0a0a0a' },
  title: { fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: 2, margin: 0 },
  subtitle: { fontSize: 11, color: '#666', marginTop: 4, marginBottom: 16 },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 16 },
  summaryCard: {
    padding: 12, background: '#111', borderRadius: 8, border: '1px solid #222',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  },
  summaryLabel: { fontSize: 8, color: '#555', fontWeight: 700, letterSpacing: 1 },
  summaryValue: { fontSize: 16, fontWeight: 900, fontFamily: "'Courier New', monospace" },
  filters: { display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' },
  filterBtn: { padding: '5px 10px', borderRadius: 4, border: '1px solid #333', fontSize: 9, fontWeight: 700, letterSpacing: 1, cursor: 'pointer' },
  txList: { background: '#111', borderRadius: 12, border: '1px solid #222', overflow: 'hidden' },
  empty: { padding: 40, textAlign: 'center', color: '#555', fontSize: 12 },
  txRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', borderBottom: '1px solid #1a1a1a',
  },
  txLeft: { display: 'flex', gap: 10, alignItems: 'center' },
  txIcon: { fontSize: 18 },
  txType: { display: 'block', fontSize: 9, color: '#DC2626', fontWeight: 700, letterSpacing: 1 },
  txDesc: { display: 'block', fontSize: 11, color: '#ccc', marginTop: 2 },
  txTime: { display: 'block', fontSize: 9, color: '#555', marginTop: 2 },
  txRight: { textAlign: 'right' },
  txAmount: { display: 'block', fontSize: 14, fontWeight: 900, fontFamily: "'Courier New', monospace" },
  txHash: { display: 'block', fontSize: 8, color: '#444', fontFamily: 'monospace', marginTop: 2 },
  footer: { textAlign: 'center', padding: '20px 0' },
  footerText: { fontSize: 10, color: '#444', lineHeight: 1.6 },
};
